import { assign, defineMachine } from 'fsm';
import { AuthStep } from '../../../stepRenderer';
import {
  KnownLowLevelAuthErrorType,
  lowLevelAlertErrorTypes,
} from './LowLevelAuthErrorAlert';
import { Params } from '../../types';
import { AuthFlow } from './types';

export type MCPParams = { clientId: string; serverUrl: string } | null;
export type OAuthParams = Map<string, string> | URLSearchParams;

export type AuthContext = {
  appId?: number;
  sessionId: string;
  steps: Array<AuthStep>;
  urlParams: Params;
  oAuthParams?: OAuthParams;
  oAuthState?: string;
  oAuthScopeSet?: string[];
  mcpParams?: MCPParams;
  customParams?: Record<string, string>;
};

export interface LowLevelAuthFlowConfig<AuthPayload, AuthResponse> {
  appId?: number;
  appName: string;
  appIcon: string;
  authenticatorId?: string;
  authFlow: AuthFlow;
  mcpParams?: MCPParams;
  oAuthScopeSet?: string[];
  customParams?: Record<string, string>;
  buildAuthPayload: (params: AuthContext) => Promise<AuthPayload>;
  makeAuthorizeUrlDescriptor: (
    payload: AuthPayload
  ) => Promise<{ authorizeUrl: string }>;
  finishAuthFlow: (payload: AuthPayload) => Promise<AuthResponse>;
}

export type LowLevelAuthFlowState<AuthResponse> =
  | {
      status: 'pending';
    }
  | {
      status: 'succeeded';
      authResponse: AuthResponse;
    };

export type LowLevelCustomAuthError = {
  type: string;
  message?: string;
};

type BuiltInLowLevelAuthFlowError =
  | {
      type: 'AuthorizeError';
      message?: string;
    }
  | {
      type: 'AuthenticateError';
      message?: string;
      upstreamErrorMessage?: string;
    }
  | {
      type:
        | 'OAuthWindowClosed'
        | 'OAuthWindowTimeout'
        | 'OAuthWindowBlocked'
        | 'UnknownOAuthProblem';
    }
  | {
      type: 'MissingPermissions';
    }
  | {
      type: 'DuplicateAccount';
      existingAccountId: number;
    };

export type LowLevelAuthFlowError<
  AuthError extends LowLevelCustomAuthError = never,
> = BuiltInLowLevelAuthFlowError | AuthError;

export type AuthProcessStatus =
  | 'idle'
  | 'authorizing'
  | 'polling'
  | 'authenticating'
  | 'cancelled'
  | 'failed';

export interface LowLevelAuthFlowMachineContext<
  AuthRequest,
  AuthResponse,
  AuthError extends LowLevelCustomAuthError = never,
> {
  portalId: number;
  /** The account being reconnected; undefined for new-account flows. */
  accountId?: number;
  params: Params;
  stepIndex: number;
  initialStepIndex: number;
  authConfig: LowLevelAuthFlowConfig<AuthRequest, AuthResponse>;
  authState: LowLevelAuthFlowState<AuthResponse>;
  authError?: LowLevelAuthFlowError<AuthError>;
  /**
   * Awaited by `useFinishAuth` before the flow is allowed to reach `success`
   * — the machine itself no longer calls this (see the FINISH_SUCCESS
   * actions below), to avoid invoking it a second time.
   */
  onComplete: (payload: AuthResponse) => void | Promise<void>;
  onError?: (error: LowLevelAuthFlowError<AuthError>) => void;
  handleDuplicateErrorsInProcessor?: boolean;
  authProcessStatus: AuthProcessStatus;
  pollingCount: number;
  oAuthState: string;
  oAuthWindow: Window | null;
  oAuthScopeOverride?: string[];
  customParamsOverride?: Record<string, string>;
}

export type LowLevelAuthFlowMachineEvent<
  AuthResponse,
  AuthError extends LowLevelCustomAuthError = never,
> =
  | { type: 'GO_TO_NEXT_STEP' }
  | { type: 'GO_TO_PREV_STEP' }
  | { type: 'UPDATE_PARAM'; name: string; value: string | Array<string> }
  | { type: 'UPDATE_SCOPES'; oAuthScopes: string[] }
  | { type: 'UPDATE_CUSTOM_PARAMS'; customParams: Record<string, string> }
  | { type: 'START_AUTH' }
  | { type: 'OAUTH_WINDOW_OPENED'; window: Window; oAuthState: string }
  | { type: 'OAUTH_WINDOW_BLOCKED' }
  | { type: 'OAUTH_WINDOW_CLOSED' }
  | { type: 'OAUTH_WINDOW_TIMEOUT' }
  | { type: 'OAUTH_RESPONSE_RECEIVED'; params: OAuthParams; state: string }
  | { type: 'START_POLLING' }
  | { type: 'POLL_TICK' }
  | { type: 'POLL_SUCCESS'; params: Map<string, string>; status: string }
  | { type: 'POLL_ERROR' }
  | { type: 'START_AUTHENTICATING' }
  | { type: 'FINISH_SUCCESS'; response: AuthResponse }
  | { type: 'FINISH_FAILURE'; error: LowLevelAuthFlowError<AuthError> }
  | { type: 'RESET_CUSTOM_ERROR' }
  | { type: 'RESET_FLOW' }
  | { type: 'CLEAR_ERROR' };

export function getLowLevelAuthFlowMachineDefinition<
  AuthRequest,
  AuthResponse,
  AuthError extends LowLevelCustomAuthError = never,
>() {
  return defineMachine<
    LowLevelAuthFlowMachineContext<AuthRequest, AuthResponse, AuthError>,
    LowLevelAuthFlowMachineEvent<AuthResponse, AuthError>
  >()({
    initial: 'flow',
    context: {} as LowLevelAuthFlowMachineContext<
      AuthRequest,
      AuthResponse,
      AuthError
    >,
    states: {
      flow: {
        on: {
          GO_TO_NEXT_STEP: {
            target: 'flow',
            cond: 'canGoToNextStep',
            actions: ['goToNextStep'],
          },
          GO_TO_PREV_STEP: {
            target: 'flow',
            cond: 'canGoToPrevStep',
            actions: ['goToPrevStep'],
          },
          UPDATE_PARAM: {
            actions: ['updateParam'],
          },
          UPDATE_SCOPES: {
            actions: ['updateScopes'],
          },
          UPDATE_CUSTOM_PARAMS: {
            actions: ['updateCustomParams'],
          },
          START_AUTH: {
            target: 'authorizing',
            actions: ['startAuthProcess'],
          },
          START_AUTHENTICATING: {
            target: 'authenticating',
            actions: ['setAuthenticatingStatus'],
          },
          FINISH_FAILURE: [
            {
              cond: 'isCustomError',
              target: 'customError',
              actions: ['setAuthError', 'reportError'],
            },
            {
              target: 'flow',
              actions: ['setAuthError', 'reportError'],
            },
          ],
          FINISH_SUCCESS: {
            target: 'success',
            actions: ['setAuthSucceeded'],
          },
          CLEAR_ERROR: {
            actions: ['clearAuthError'],
          },
        },
      },
      authorizing: {
        on: {
          OAUTH_WINDOW_OPENED: {
            actions: ['setOAuthWindow'],
          },
          OAUTH_WINDOW_BLOCKED: {
            target: 'flow',
            actions: ['setAuthFailed', 'setOAuthWindowBlockedError'],
          },
          OAUTH_WINDOW_TIMEOUT: {
            target: 'flow',
            actions: [
              'setAuthCancelled',
              'setOAuthTimeoutError',
              'closeOAuthWindow',
            ],
          },
          OAUTH_WINDOW_CLOSED: {
            target: 'flow',
            actions: ['cancelAuthAsWindowClosed'],
          },
          START_AUTHENTICATING: {
            target: 'authenticating',
            actions: ['setAuthenticatingStatus'],
          },
          START_POLLING: {
            target: 'polling',
            actions: ['setPollingStatus'],
          },
          OAUTH_RESPONSE_RECEIVED: [
            {
              cond: 'isValidOAuthState',
              target: 'authenticating',
              actions: ['setAuthenticatingStatus'],
            },
            {
              target: 'flow',
              actions: ['setAuthFailed', 'setUnknownOAuthError'],
            },
          ],
          FINISH_FAILURE: [
            {
              cond: 'isCustomError',
              target: 'customError',
              // resetAuthProcess must run before setAuthError: both assign
              // authProcessStatus, and actions merge in array order, so
              // resetAuthProcess's 'idle' would otherwise clobber
              // setAuthError's 'failed' and the button would render as if
              // nothing failed (and get stuck there with keepResultAfterFinish).
              actions: ['resetAuthProcess', 'setAuthError', 'reportError'],
            },
            {
              target: 'flow',
              // resetAuthProcess must run before setAuthError: both assign
              // authProcessStatus, and actions merge in array order, so
              // resetAuthProcess's 'idle' would otherwise clobber
              // setAuthError's 'failed' and the button would render as if
              // nothing failed (and get stuck there with keepResultAfterFinish).
              actions: ['resetAuthProcess', 'setAuthError', 'reportError'],
            },
          ],
          FINISH_SUCCESS: {
            target: 'success',
            actions: ['setAuthSucceeded', 'resetAuthProcess'],
          },
        },
      },
      polling: {
        on: {
          POLL_TICK: {
            actions: ['incrementPollingCount'],
          },
          POLL_SUCCESS: [
            {
              cond: 'isPollResponseReady',
              target: 'authenticating',
              actions: ['setAuthenticatingStatus'],
            },
            {
              target: 'polling',
            },
          ],
          POLL_ERROR: {
            target: 'flow',
            actions: ['setAuthFailed', 'setUnknownOAuthError'],
          },
          OAUTH_RESPONSE_RECEIVED: [
            {
              cond: 'isValidOAuthState',
              target: 'authenticating',
              actions: ['setAuthenticatingStatus'],
            },
            {
              target: 'flow',
              actions: ['setAuthFailed', 'setUnknownOAuthError'],
            },
          ],
          OAUTH_WINDOW_TIMEOUT: {
            target: 'flow',
            actions: [
              'setAuthCancelled',
              'setOAuthTimeoutError',
              'closeOAuthWindow',
            ],
          },
          // The popup closing is not a reliable signal in OAuthWithPolling: a 3p
          // with restrictive CORS/COOP severs the parent's access to the popup, so
          // `window.closed` can read true while the user is still mid-auth, and the
          // oauth-redirect page deliberately closes the popup after persisting the
          // authorization code for polling to retrieve. Ignore it and keep polling
          // until a poll resolves or the session times out.
          OAUTH_WINDOW_CLOSED: {},
          START_AUTH: {
            target: 'authorizing',
            actions: ['startAuthProcess'],
          },
          FINISH_SUCCESS: {
            target: 'success',
            actions: ['setAuthSucceeded', 'resetAuthProcess'],
          },
          FINISH_FAILURE: [
            {
              cond: 'isCustomError',
              target: 'customError',
              // resetAuthProcess must run before setAuthError: both assign
              // authProcessStatus, and actions merge in array order, so
              // resetAuthProcess's 'idle' would otherwise clobber
              // setAuthError's 'failed' and the button would render as if
              // nothing failed (and get stuck there with keepResultAfterFinish).
              actions: ['resetAuthProcess', 'setAuthError', 'reportError'],
            },
            {
              target: 'flow',
              // resetAuthProcess must run before setAuthError: both assign
              // authProcessStatus, and actions merge in array order, so
              // resetAuthProcess's 'idle' would otherwise clobber
              // setAuthError's 'failed' and the button would render as if
              // nothing failed (and get stuck there with keepResultAfterFinish).
              actions: ['resetAuthProcess', 'setAuthError', 'reportError'],
            },
          ],
        },
      },
      authenticating: {
        on: {
          // Ignore polling events while authenticating - polling may still be running
          POLL_TICK: {},
          POLL_SUCCESS: {},
          POLL_ERROR: {},
          // Ignore OAUTH_RESPONSE_RECEIVED - for polling flow, POLL_SUCCESS already transitioned here
          OAUTH_RESPONSE_RECEIVED: {},
          FINISH_SUCCESS: {
            target: 'success',
            actions: ['setAuthSucceeded', 'resetAuthProcess'],
          },
          FINISH_FAILURE: [
            {
              cond: 'isCustomError',
              target: 'customError',
              // resetAuthProcess must run before setAuthError: both assign
              // authProcessStatus, and actions merge in array order, so
              // resetAuthProcess's 'idle' would otherwise clobber
              // setAuthError's 'failed' and the button would render as if
              // nothing failed (and get stuck there with keepResultAfterFinish).
              actions: ['resetAuthProcess', 'setAuthError', 'reportError'],
            },
            {
              target: 'flow',
              // resetAuthProcess must run before setAuthError: both assign
              // authProcessStatus, and actions merge in array order, so
              // resetAuthProcess's 'idle' would otherwise clobber
              // setAuthError's 'failed' and the button would render as if
              // nothing failed (and get stuck there with keepResultAfterFinish).
              actions: ['resetAuthProcess', 'setAuthError', 'reportError'],
            },
          ],
        },
      },
      customError: {
        on: {
          RESET_CUSTOM_ERROR: {
            target: 'flow',
            actions: ['resetAfterCustomError'],
          },
        },
      },
      success: {
        on: {
          RESET_FLOW: {
            target: 'flow',
            actions: ['resetFlowState'],
          },
        },
      },
    },
    actions: {
      goToNextStep: assign((context) => ({
        stepIndex: Math.min(
          context.stepIndex + 1,
          context.authConfig.authFlow.steps.length - 1
        ),
      })),
      goToPrevStep: assign((context) => ({
        stepIndex: Math.max(context.stepIndex - 1, context.initialStepIndex),
      })),
      updateParam: assign((context, event) => {
        if (event.type !== 'UPDATE_PARAM') {
          return {};
        }

        return {
          params: {
            ...context.params,
            [event.name]: event.value,
          },
        };
      }),
      updateScopes: assign((_context, event) => {
        if (event.type !== 'UPDATE_SCOPES') {
          return {};
        }

        return { oAuthScopeOverride: event.oAuthScopes };
      }),
      updateCustomParams: assign((_context, event) => {
        if (event.type !== 'UPDATE_CUSTOM_PARAMS') {
          return {};
        }

        return { customParamsOverride: event.customParams };
      }),
      startAuthProcess: assign(() => ({
        authProcessStatus: 'authorizing' as const,
        pollingCount: 0,
        authError: undefined,
      })),
      setOAuthWindow: assign((_context, event) => {
        if (event.type !== 'OAUTH_WINDOW_OPENED') {
          return {};
        }
        return {
          oAuthWindow: event.window,
          oAuthState: event.oAuthState,
        };
      }),
      setPollingStatus: assign(() => ({
        authProcessStatus: 'polling' as const,
      })),
      setAuthenticatingStatus: assign(() => ({
        authProcessStatus: 'authenticating' as const,
      })),
      incrementPollingCount: assign((context) => ({
        pollingCount: context.pollingCount + 1,
      })),
      setAuthFailed: assign(() => ({
        authProcessStatus: 'failed' as const,
      })),
      setAuthCancelled: assign(() => ({
        authProcessStatus: 'cancelled' as const,
      })),
      setOAuthWindowBlockedError: assign(() => ({
        authError: { type: 'OAuthWindowBlocked' as const },
      })),
      setOAuthTimeoutError: assign(() => ({
        authError: { type: 'OAuthWindowTimeout' as const },
      })),
      setUnknownOAuthError: assign(() => ({
        authError: { type: 'UnknownOAuthProblem' as const },
      })),
      closeOAuthWindow: (context) => {
        if (context.oAuthWindow) {
          context.oAuthWindow.close();
        }
      },
      cancelAuthAsWindowClosed: assign(() => ({
        authProcessStatus: 'cancelled' as const,
        authError: { type: 'OAuthWindowClosed' as const },
        oAuthWindow: null,
        oAuthState: '',
      })),
      resetAuthProcess: assign(() => ({
        authProcessStatus: 'idle' as const,
        pollingCount: 0,
        oAuthWindow: null,
        oAuthState: '',
      })),
      setAuthSucceeded: assign((_context, event) => {
        if (event.type !== 'FINISH_SUCCESS') {
          return {};
        }

        return {
          authState: {
            status: 'succeeded',
            authResponse: event.response,
          },
          authError: undefined,
          authProcessStatus: 'idle' as const,
        };
      }),
      setAuthError: assign((_context, event) => {
        if (event.type !== 'FINISH_FAILURE') {
          return {};
        }

        return {
          authError: event.error,
          authProcessStatus: 'failed' as const,
        };
      }),
      // Deliberately does not call context.onComplete: useFinishAuth already
      // awaited it before dispatching FINISH_SUCCESS, so the flow only gets
      // here once that hook has resolved. Calling it again would invoke the
      // consumer's completion hook (and any side effects, e.g. an installRpc
      // call) a second time.
      reportError: (context, event) => {
        if (event.type === 'FINISH_FAILURE') {
          context.onError?.(event.error);
        }
      },
      resetAfterCustomError: assign((context) => ({
        authError: undefined,
        stepIndex: context.initialStepIndex,
        authProcessStatus: 'idle' as const,
        pollingCount: 0,
        oAuthWindow: null,
        oAuthState: '',
        oAuthScopeOverride: undefined,
        customParamsOverride: undefined,
      })),
      resetFlowState: assign((context) => ({
        authError: undefined,
        authState: { status: 'pending' },
        stepIndex: context.initialStepIndex,
        authProcessStatus: 'idle' as const,
        pollingCount: 0,
        oAuthWindow: null,
        oAuthState: '',
        oAuthScopeOverride: undefined,
        customParamsOverride: undefined,
      })),
      clearAuthError: assign(() => ({
        authError: undefined,
      })),
    },
    guards: {
      canGoToNextStep: (context) => {
        const { inline = false, steps } = context.authConfig.authFlow;
        return !inline && context.stepIndex < steps.length - 1;
      },
      canGoToPrevStep: (context) => {
        const { inline = false } = context.authConfig.authFlow;
        return !inline && context.stepIndex > context.initialStepIndex;
      },
      isCustomError: (_context, event) =>
        event.type === 'FINISH_FAILURE' &&
        !lowLevelAlertErrorTypes.includes(
          event.error.type as KnownLowLevelAuthErrorType
        ),
      isValidOAuthState: (context, event) => {
        if (event.type !== 'OAUTH_RESPONSE_RECEIVED') {
          return false;
        }
        return event.state === context.oAuthState;
      },
      isPollResponseReady: (_context, event) => {
        if (event.type !== 'POLL_SUCCESS') {
          return false;
        }
        return event.status !== 'PENDING';
      },
    },
  });
}
